<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Payee/Depositor/Party List
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                        >
                          CSV
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                        >
                          EXCEL
                        </v-btn>
                        <v-btn
                          prepend-icon="mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                        >
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right">
                      <v-btn
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-sync"
                        rounded="xl"
                        @click="syncParties"
                        :loading="syncing"
                      >
                        Sync Parties
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="headers"
                  :items="getPartyList"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                  class="elevation-1"
                >
                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.type="{ item }">
                    <v-chip
                      :color="getTypeColor(item.type)"
                      size="small"
                      class="text-capitalize"
                    >
                      <v-icon start size="small">{{ getTypeIcon(item.type) }}</v-icon>
                      {{ item.type }}
                    </v-chip>
                  </template>

                  <template v-slot:no-data>
                    <v-alert type="info" class="ma-4">
                      No parties found. Click "Sync Parties" to load data from Customers, Suppliers, and Employees.
                    </v-alert>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { toast } from "vue3-toastify";

export default {
  name: "PayeeDepositorParty",

  data: () => ({
    search: "",
    loading: false,
    syncing: false,
    itemsPerPage: 10,
    panel: [0],
    headers: [
      {
        title: "#SL",
        align: "center",
        key: "index",
        width: "5%",
        sortable: false,
      },
      { title: "Code", key: "code", align: "center", sortable: false },
      { title: "Name", key: "name", align: "left", sortable: false },
      { title: "Type", key: "type", align: "center", sortable: false },
    ],
  }),

  created() {
    // if (this.$store.getters.getPartyList.length === 0) {
      this.loading = true;
      this.$store.dispatch("getPartyList").finally(() => {
        this.loading = false;
      });
    // }
  },

  computed: {
    getPartyList() {
      return this.$store.getters.getPartyList || [];
    },
  },

  methods: {
    async syncParties() {
      try {
        this.syncing = true;
        const response = await this.$store.dispatch("syncPartyList");

        if (response.data.success) {
          toast.success(response.data.message || "Parties synced successfully");
          // Reload the list
          this.loading = true;
          await this.$store.dispatch("getPartyList");
          this.loading = false;
        } else {
          toast.error(response.data.message || "Failed to sync parties");
        }
      } catch (error) {
        console.error("Error syncing parties:", error);
        toast.error(error.response?.data?.message || "Failed to sync parties");
      } finally {
        this.syncing = false;
      }
    },

    getTypeColor(type) {
      const colorMap = {
        Customer: "success",
        Supplier: "info",
        Employee: "warning",
        Other: "grey",
      };
      return colorMap[type] || "grey";
    },

    getTypeIcon(type) {
      const iconMap = {
        Customer: "mdi-account-group",
        Supplier: "mdi-truck-delivery",
        Employee: "mdi-account-tie",
        Other: "mdi-help-circle",
      };
      return iconMap[type] || "mdi-help-circle";
    },

    exportToPDF() {
      let serialNumber = 1;
      const columns = [
        "#SL",
        "Code",
        "Name",
        "Type",
      ];
      const rows = this.getPartyList.map((item) => [
        serialNumber++,
        item.code || "-",
        item.name || "-",
        item.type || "-",
      ]);
      this.$exportTableToPDF(
        columns,
        rows,
        "Payee/Depositor/Party List",
        "payee_depositor_party_list_" + this.$fileDate(),
        "portrait"
      );
    },

    exportToExcel() {
      var data = [
        [
          "#SL",
          "Code",
          "Name",
          "Type",
        ],
      ];
      var serialNumber = 1;
      this.getPartyList.forEach((item) => {
        data.push([
          serialNumber++,
          item.code || "-",
          item.name || "-",
          item.type || "-",
        ]);
      });
      this.$exportTableToExcel(
        data,
        "payee_depositor_party_list_" + this.$fileDate()
      );
    },

    exportToCSV() {
      var data = [
        [
          "#SL",
          "Code",
          "Name",
          "Type",
        ],
      ];
      var serialNumber = 1;
      this.getPartyList.forEach((item) => {
        data.push([
          serialNumber++,
          item.code || "-",
          item.name || "-",
          item.type || "-",
        ]);
      });

      this.$exportTableToCSV(
        data,
        "payee_depositor_party_list_" + this.$fileDate()
      );
    },
  },

  beforeUnmount() {
    this.$store.commit("UNSET_PARTY_LIST");
  },
};
</script>

<style scoped>
.edit-button {
  cursor: pointer;
}

.edit-button:hover {
  opacity: 0.7;
}
</style>

